// THB pricing helpers. Stripe expects amounts in the smallest currency unit;
// THB is 2-decimal so 7 THB = 700 satang. Everything user-facing reads from
// the admin-managed `settings.pricing` block — never hardcode in callers.

export function thbToSatang(thb: number): number {
  if (!Number.isFinite(thb) || thb < 0) throw new Error(`Invalid THB amount: ${thb}`);
  return Math.round(thb * 100);
}

export function satangToThb(satang: number): number {
  if (!Number.isFinite(satang) || satang < 0) throw new Error(`Invalid satang amount: ${satang}`);
  return Math.round(satang) / 100;
}

export function formatThb(
  thb: number,
  opts: { locale?: "th" | "en"; withCurrency?: boolean } = {},
): string {
  const { locale = "en", withCurrency = true } = opts;
  const formatted = new Intl.NumberFormat(locale === "th" ? "th-TH" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(thb);
  if (!withCurrency) return formatted;
  return locale === "th" ? `${formatted} บาท` : `฿${formatted}`;
}

// Resolve the effective unit price for an episode given the active promo window
// and any series-level overrides.
export interface EffectivePriceInput {
  defaultEpisodePriceThb: number;
  defaultBundlePriceThb: number;
  launchPromoPriceThb: number;
  launchPromoEndAt: Date | string | null;
  seriesBundlePriceThb?: number | null;
  episodeOverridePriceThb?: number | null;
}

export function effectiveEpisodePrice(input: EffectivePriceInput, now: Date = new Date()): number {
  if (typeof input.episodeOverridePriceThb === "number") return input.episodeOverridePriceThb;
  if (input.launchPromoEndAt) {
    const end =
      typeof input.launchPromoEndAt === "string"
        ? new Date(input.launchPromoEndAt)
        : input.launchPromoEndAt;
    if (Number.isFinite(end.getTime()) && end.getTime() > now.getTime()) {
      return input.launchPromoPriceThb;
    }
  }
  return input.defaultEpisodePriceThb;
}

export function effectiveBundlePrice(input: EffectivePriceInput): number {
  if (typeof input.seriesBundlePriceThb === "number") return input.seriesBundlePriceThb;
  return input.defaultBundlePriceThb;
}
